import { useState, useEffect, useRef } from 'react';
import { getAvailableSlots } from '../utils/api';
import { toApiDate, formatDate, isPastDate, getDayName, getMonthName } from '../utils/date';
import './Calendar.css';

const Calendar = ({ selectedService, onDateTimeSelect, onBack, onNext, selectedDate, selectedTime }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(selectedDate || null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const [daysAvailability, setDaysAvailability] = useState({}); 
  const availabilityRequestRef = useRef(0);

  useEffect(() => {
    if (selectedDay && selectedService) {
      loadAvailableSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, selectedService]);

  useEffect(() => {
    if (selectedService && currentMonth) {
      loadMonthAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, selectedService]);

  const loadAvailableSlots = async () => {
    if (!selectedDay || !selectedService) return;

    try {
      setLoadingSlots(true);
      const dateStr = toApiDate(selectedDay);
      const response = await getAvailableSlots(dateStr, selectedService.id);
      const slots = response.data.data?.slots || [];
      setAvailableSlots(slots);
      
      const availableCount = slots.filter(s => s.available).length;
      const totalCount = slots.length;
      let status = 'full';
      if (totalCount > 0) {
        if (availableCount === totalCount) status = 'available';
        else if (availableCount > 0) status = 'partial';
      }
      setDaysAvailability(prev => ({ ...prev, [dateStr]: status }));
    } catch (error) {
      console.error('Failed to load slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const getDayIndicatorClass = (day) => {
    const dateStr = toApiDate(day);
    const status = daysAvailability[dateStr];
    
    if (day.toDateString() === new Date().toDateString()) {
      return 'indicator-today';
    }
    
    switch (status) {
      case 'available': return 'indicator-available';
      case 'partial': return 'indicator-partial';
      case 'full': return 'indicator-full';
      default: return '';
    }
  };

  const loadMonthAvailability = async () => {
    if (!selectedService) return;
    const requestId = ++availabilityRequestRef.current;
    const days = getDaysInMonth(currentMonth);
    const futureDays = days.filter(day => day && !isPastDate(day));

    futureDays.forEach(async (day) => {
      const dateStr = toApiDate(day);
      try {
        const response = await getAvailableSlots(dateStr, selectedService.id);
        const slots = response.data.data?.slots || [];
        const availableCount = slots.filter(s => s.available).length;
        const totalCount = slots.length;
        
        let status = 'full';
        if (totalCount > 0) {
          if (availableCount === totalCount) status = 'available';
          else if (availableCount > 0) status = 'partial';
        }

        if (availabilityRequestRef.current !== requestId) return;
        setDaysAvailability(prev => ({ ...prev, [dateStr]: status }));
      } catch (error) {
        if (availabilityRequestRef.current !== requestId) return;
        setDaysAvailability(prev => ({ ...prev, [dateStr]: 'full' }));
      }
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const handlePrevMonth = () => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
    const today = new Date();
    
    if (prevMonth.getFullYear() < today.getFullYear() || 
        (prevMonth.getFullYear() === today.getFullYear() && prevMonth.getMonth() < today.getMonth())) {
      return;
    }
    
    setCurrentMonth(prevMonth);
  };

  const canGoPrevMonth = () => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
    const today = new Date();
    
    return prevMonth.getFullYear() > today.getFullYear() || (prevMonth.getFullYear() === today.getFullYear() && prevMonth.getMonth() >= today.getMonth());
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDayClick = (day) => {
    if (!day || isPastDate(day)) return;
    setSelectedDay(day);
  };

  const handleTimeClick = (time) => {
    if (selectedDay) {
      onDateTimeSelect(selectedDay, time);
      if (onNext) {
        onNext();
      }
    }
  };

  const filteredSlots = showOnlyAvailable 
    ? availableSlots.filter(slot => {
        const isAvailable = typeof slot === 'string' ? true : slot.available;
        return isAvailable;
      })
    : availableSlots;

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="calendar-container">
      <div className="calendar-grid">
        {/* Left Side - Calendar */}
        <div className="calendar-panel">
          <div className="calendar-header">
            <button 
              onClick={handlePrevMonth} 
              className="calendar-nav-btn"
              disabled={!canGoPrevMonth()}
            >
              ‹
            </button>
            <h3 className="calendar-month">
              {getMonthName(currentMonth)} {currentMonth.getFullYear()}
            </h3>
            <button onClick={handleNextMonth} className="calendar-nav-btn">
              ›
            </button>
          </div>

          <div className="calendar-weekdays">
            {['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne'].map(day => (
              <div key={day} className="weekday">{day}</div>
            ))}
          </div>

          <div className="calendar-days">
            {days.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="calendar-day empty"></div>;
              }

              const isSelected = selectedDay && day.toDateString() === selectedDay.toDateString();
              const isPast = isPastDate(day);
              const isToday = day.toDateString() === new Date().toDateString();

              return (
                <div
                  key={index}
                  className={`calendar-day ${isSelected ? 'selected' : ''} ${isPast ? 'past' : ''} ${isToday ? 'today' : ''}`}
                  onClick={() => handleDayClick(day)}
                >
                  <span className="day-number">{day.getDate()}</span>
                  {!isPast && <span className={`day-indicator ${getDayIndicatorClass(day)}`}></span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Side - Time Slots */}
        <div className="time-panel">
          {selectedDay ? (
            <>
              <div className="time-header">
                <h3 className="time-title">
                  {getDayName(selectedDay)}, {formatDate(selectedDay)}
                </h3>
                <button
                  className={`filter-available-btn ${showOnlyAvailable ? 'active' : ''}`}
                  onClick={() => setShowOnlyAvailable(!showOnlyAvailable)}
                  title={showOnlyAvailable ? 'Zobraziť všetky termíny' : 'Zobraziť iba voľné termíny'}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                  </svg>
                  Iba voľné
                </button>
              </div>

              {loadingSlots ? (
                <div className="time-loading">
                  <div className="spinner"></div>
                  <p>Načítavam dostupné časy...</p>
                </div>
              ) : filteredSlots.length > 0 ? (
                <div className="time-slots">
                  {filteredSlots.map((slot) => {
                    const timeValue = typeof slot === 'string' ? slot : slot.time;
                    const isAvailable = typeof slot === 'string' ? true : slot.available;
                    const reason = typeof slot === 'object' ? slot.reason : null;
                    const tooltipText = reason === 'max_advance_exceeded' 
                      ? 'Presiahli ste maximálny predstih, vyberte si skorší dátum' 
                      : null;
                    
                    return (
                      <button
                        key={timeValue}
                        className={`time-slot ${selectedTime === timeValue ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                        onClick={() => isAvailable && handleTimeClick(timeValue)}
                        disabled={!isAvailable}
                        title={tooltipText || ''}
                      >
                        {timeValue}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-slots">
                  {showOnlyAvailable ? (
                    <>
                      <p>V tento deň nie sú žiadne voľné termíny.</p>
                      <button 
                        onClick={() => setShowOnlyAvailable(false)}
                        className="btn btn-secondary btn-sm"
                        style={{ marginTop: '1rem' }}
                      >
                        Zobraziť všetky termíny
                      </button>
                    </>
                  ) : (
                    <>
                      <p>V tento deň nie je pracovná doba.</p>
                      <p className="text-muted text-sm">Skúste vybrať iný dátum.</p>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="no-date-selected">
              <p>Vyberte dátum pre zobrazenie dostupných časov</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      {onBack && (
        <div className="calendar-actions">
          <button onClick={onBack} className="btn btn-secondary">
            Späť
          </button>
        </div>
      )}
    </div>
  );
};

export default Calendar;
